/**
 * Once-only onboarding flows (Geri, #96): the profile wizard and the tutorial.
 *
 * Stored on `users/{uid}/private/settings.onboarding` (docs/05) — owner-only,
 * client-written. `profileWizardAt` / `tutorialAt` = when the user closed the
 * flow (finished, skipped or dismissed). A missing field = not seen yet, so it
 * shows once — for new users after sign-up and for existing users on the first
 * start after the release.
 */

import { doc, onSnapshot, serverTimestamp, setDoc, type Timestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';

export type OnboardingFlow = 'profileWizardAt' | 'tutorialAt';

export interface OnboardingState {
  /** The settings document has answered at least once. */
  loaded: boolean;
  profileWizardAt: number | null;
  tutorialAt: number | null;
}

export const ONBOARDING_NOT_LOADED: OnboardingState = {
  loaded: false,
  profileWizardAt: null,
  tutorialAt: null,
};

function toMillis(value: unknown): number | null {
  if (value && typeof (value as Timestamp).toMillis === 'function') return (value as Timestamp).toMillis();
  return typeof value === 'number' ? value : null;
}

/** Reads the `onboarding` map of the settings document. */
export function parseOnboarding(data: Record<string, unknown> | undefined): OnboardingState {
  const onboarding = (data?.onboarding ?? {}) as Record<string, unknown>;
  return {
    loaded: true,
    profileWizardAt: toMillis(onboarding.profileWizardAt),
    tutorialAt: toMillis(onboarding.tutorialAt),
  };
}

export function subscribeOnboarding(uid: string, onChange: (state: OnboardingState) => void): () => void {
  if (!db) return () => {};
  return onSnapshot(
    doc(db, 'users', uid, 'private', 'settings'),
    // 'estimate': the local echo of our own `serverTimestamp()` write must
    // already count as closed, or the flow would flash up again until the ack.
    (snapshot) => onChange(parseOnboarding(snapshot.data({ serverTimestamps: 'estimate' }))),
    // Unreadable (offline without cache, rules) → never block the app with it.
    () => {},
  );
}

/** Closes a flow for good; it only comes back from Settings. */
export async function markOnboardingDone(uid: string, flow: OnboardingFlow): Promise<void> {
  if (!db) return;
  await setDoc(
    doc(db, 'users', uid, 'private', 'settings'),
    { onboarding: { [flow]: serverTimestamp() } },
    { merge: true },
  );
}
